import React from "react";
import classNames from "classnames";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import "./Tooltip.scss";

/**
 * Props for the Tooltip component.
 *
 * @description The Tooltip component is a dismissible tooltip/popup with a close button.
 * It displays content with a small close icon for dismissal.
 *
 * @property {string} [className] - Additional CSS class names to apply to the tooltip.
 * @property {React.ReactNode} [children] - Content to display inside the tooltip.
 * @property {() => void} [onClickClose] - Function called when the close button is clicked.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic tooltip
 * <Tooltip onClickClose={() => setVisible(false)}>
 *   <span>Tooltip content here</span>
 * </Tooltip>
 *
 * @example
 * // Tooltip with custom styling
 * <Tooltip
 *   className="info-tooltip"
 *   onClickClose={handleClose}
 *   testId="info-tooltip"
 * >
 *   <p>Important information</p>
 * </Tooltip>
 */
interface Props {
  className?: string;
  children?: React.ReactNode;
  onClickClose?: () => void;
  testId?: string;
}

/**
 * A dismissible tooltip component with close button.
 *
 * @description Tooltip renders a small popup with content and a close button.
 * It's useful for displaying contextual information, hints, or notifications
 * that can be dismissed by the user.
 *
 * Features:
 * - Close button with small X icon
 * - Flexible content via children prop
 * - Custom styling via className prop
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A tooltip div with close button and content
 *
 * @see Props for all available properties
 */
export const Tooltip: React.FC<Props> = (props) => {
  const { className, children, onClickClose, testId } = props;
  const wrapClass = classNames("tooltip", className);

  const handleOnClickClose = () => {
    if (onClickClose) {
      onClickClose();
    }
  };

  return (
    <div className={wrapClass} data-testid={testId}>
      <div className="tooltip__close" onClick={handleOnClickClose}>
        <Svg icon={SVG_ICONS.CLOSE_SMALL} width={12} height={12} />
      </div>
      {children}
    </div>
  );
};
